import React, { useEffect, useState } from 'react';
import { ErrorState } from './Feedback';

// ErrorScreen — full-screen connection-trouble surface. Replaces the
// previous behavior where the LoadingScreen hung indefinitely if the
// initial data load failed (the May 19 incident bug — Supabase gateway
// was 522'ing and the app sat on the red splash for 20+ hours).
//
// feedback.md 4B (2026-09-15): renders the shared dashed ErrorState card
// so a failed boot looks like a failed panel, just centered on the page.
//
// Props:
//   message  — short string describing what went wrong.
//   onRetry  — fires the manual "Retry" button. Required.
//   retryInfo — optional auto-retry status from StoreContext. Shape:
//               { attempt, maxAttempts, nextAttemptAt }
//               When present we render a live countdown to the next
//               attempt + an attempt counter, so the user knows the
//               app is actively healing itself.
export default function ErrorScreen({ message, onRetry, retryInfo }) {
  // Live countdown to next auto-retry. Re-renders every second so the
  // "Retrying in 4s..." text actually ticks down.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!retryInfo?.nextAttemptAt) return;
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [retryInfo?.nextAttemptAt]);

  const secondsUntilRetry = retryInfo?.nextAttemptAt
    ? Math.max(0, Math.ceil((retryInfo.nextAttemptAt - now) / 1000))
    : null;

  const retryLine = retryInfo
    ? ` Retrying automatically — attempt ${retryInfo.attempt} of ${retryInfo.maxAttempts}${secondsUntilRetry > 0 ? `, next in ${secondsUntilRetry}s` : ', retrying now'}.`
    : '';

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <ErrorState
          title="Connection trouble"
          hint={`${message || 'Couldn’t reach the server.'}${retryLine} This usually clears up on its own within a few minutes; if it keeps happening, check status.supabase.com.`}
          onRetry={onRetry}
          retryLabel="Retry now"
          showReload={false}
        />
      </div>
    </div>
  );
}
